import { registerPrintAreaShape } from './extensionRegistry';
import { t } from '../shared/i18n';

function roundedRectClip(ctx: CanvasRenderingContext2D, w: number, h: number, r: number): void {
  ctx.moveTo(r, 0);
  ctx.lineTo(w - r, 0);
  ctx.quadraticCurveTo(w, 0, w, r);
  ctx.lineTo(w, h - r);
  ctx.quadraticCurveTo(w, h, w - r, h);
  ctx.lineTo(r, h);
  ctx.quadraticCurveTo(0, h, 0, h - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
}

let bootstrapped = false;

/**
 * Register the built-in print-area shapes (rect/rounded/circle) through the
 * same registry an addon would use, so the extension point stays honest.
 */
export function bootstrapBuiltinPrintAreaShapes(): void {
  if (bootstrapped) return;
  bootstrapped = true;

  registerPrintAreaShape({
    id: 'rect',
    label: t('Rectangle'),
    description: t('Standard rectangular print area. Best for chest prints, posters and most products.'),
    priority: 0,
    cssStyle: () => ({}),
    clipCanvas: (ctx, w, h) => {
      ctx.rect(0, 0, w, h);
    },
    clipSvgFragment: (w, h) => `<rect x="0" y="0" width="${w}" height="${h}"/>`,
  });

  registerPrintAreaShape({
    id: 'rounded',
    label: t('Rounded rectangle'),
    description: t('Rectangle with softened corners. Good for stickers, patches and labels; set the corner radius below.'),
    priority: 10,
    paramsSchema: {
      fields: [{ key: 'radiusPx', label: t('Corner radius (px)'), type: 'number', min: 0, default: 16 }],
    },
    cssStyle: (params) => ({ borderRadius: `${Number(params.radiusPx) || 0}px` }),
    clipCanvas: (ctx, w, h, params) => {
      const r = Math.min(Number(params.radiusPx) || 0, w / 2, h / 2);
      roundedRectClip(ctx, w, h, r);
    },
    clipSvgFragment: (w, h, params) => {
      const r = Math.min(Number(params.radiusPx) || 0, w / 2, h / 2);
      return `<rect x="0" y="0" width="${w}" height="${h}" rx="${r}" ry="${r}"/>`;
    },
  });

  registerPrintAreaShape({
    id: 'circle',
    label: t('Circle / ellipse'),
    description: t('Artwork is cropped to a circle or ellipse. Good for badges, buttons and round logos.'),
    priority: 20,
    cssStyle: () => ({ borderRadius: '50%' }),
    clipCanvas: (ctx, w, h) => {
      ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    },
    clipSvgFragment: (w, h) => `<ellipse cx="${w / 2}" cy="${h / 2}" rx="${w / 2}" ry="${h / 2}"/>`,
  });
}
